// src/store/employeeActions.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import employeeServiceApiInstance from '../../../config/employeeApiConfig';
import { CreateEmployeeResponse } from './employeeTypes';


// Define a thunk for creating an employee
export const createEmployee = createAsyncThunk<CreateEmployeeResponse, any, { rejectValue: string }>(
  'employee/createEmployee',
  async (employeeData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Append only defined fields
      employeeData.FirstName !== undefined && formData.append('FirstName', employeeData.FirstName);
      employeeData.LastName !== undefined && formData.append('LastName', employeeData.LastName);
      employeeData.Email !== undefined && formData.append('Email', employeeData.Email);
      employeeData.mobile !== undefined && formData.append('mobile', employeeData.mobile);
      employeeData.password !== undefined && formData.append('password', employeeData.password);
      employeeData.DateOfBirth !== undefined && formData.append('DateOfBirth', employeeData.DateOfBirth);
      employeeData.GenderId !== undefined && formData.append('GenderId', employeeData.GenderId);
      employeeData.DesignationId !== undefined && formData.append('DesignationId', employeeData.DesignationId);
      employeeData.DepartmentId !== undefined && formData.append('DepartmentId', employeeData.DepartmentId);
      employeeData.DateOfJoining !== undefined && formData.append('DateOfJoining', employeeData.DateOfJoining);
      employeeData.Description !== undefined && formData.append('Description', employeeData.Description);
      employeeData.SupervisorId !== undefined && formData.append('SupervisorId', employeeData.SupervisorId);
      employeeData.Company !== undefined && formData.append('Company', employeeData.Company);
      employeeData.RoleId !== undefined && formData.append('RoleId', employeeData.RoleId);
      employeeData.employmentType !== undefined && formData.append('employmentType', employeeData.employmentType);
      employeeData.workPreference !== undefined && formData.append('workPreference', employeeData.workPreference);
      employeeData.employee_status !== undefined && formData.append('employee_status', employeeData.employee_status);
      employeeData.pfEnable !== undefined && formData.append('pfEnable', employeeData.pfEnable);
      employeeData.job_location !== undefined && formData.append('job_location', employeeData.job_location);
      employeeData.branch !== undefined && formData.append('branch', employeeData.branch);
      employeeData.checked !== undefined && formData.append('checked', employeeData.checked);
      employeeData.company_details_id !== undefined && formData.append('company_details_id', employeeData.company_details_id);
      employeeData.financial_year_id !== undefined && formData.append('financial_year_id', employeeData.financial_year_id);
      employeeData.locationTracking !== undefined && formData.append('locationTracking', employeeData.locationTracking);
      employeeData.experiencelabel !== undefined && formData.append('experiencelabel', employeeData.experiencelabel);
      employeeData.maxUser !== undefined && formData.append('maxUser', employeeData.maxUser);
      employeeData.hr !== undefined && formData.append('hr', employeeData.hr);
      employeeData.hod_id !== undefined && formData.append('hod_id', employeeData.hod_id);

      if (employeeData.profilePicture) {
        formData.append('file', employeeData.profilePicture);
      }

      const response = await employeeServiceApiInstance.post('/employees', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating employee');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);


export const updateEmployee = createAsyncThunk<
  any,
  { employeeId: number; employeeData: any },
  { rejectValue: string }
>(
  'employee/updateEmployee',
  async ({ employeeId, employeeData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      employeeData.FirstName !== undefined && formData.append('FirstName', employeeData.FirstName);
      employeeData.LastName !== undefined && formData.append('LastName', employeeData.LastName);
      employeeData.Email !== undefined && formData.append('Email', employeeData.Email);
      employeeData.mobile !== undefined && formData.append('mobile', employeeData.mobile);
      employeeData.DateOfBirth !== undefined && formData.append('DateOfBirth', employeeData.DateOfBirth);
      employeeData.GenderId !== undefined && formData.append('GenderId', employeeData.GenderId);
      employeeData.DesignationId !== undefined && formData.append('DesignationId', employeeData.DesignationId);
      employeeData.DepartmentId !== undefined && formData.append('DepartmentId', employeeData.DepartmentId);
      employeeData.DateOfJoining !== undefined && formData.append('DateOfJoining', employeeData.DateOfJoining);
      employeeData.Description !== undefined && formData.append('Description', employeeData.Description);
      employeeData.SupervisorId !== undefined && formData.append('SupervisorId', employeeData.SupervisorId);
      employeeData.Company !== undefined && formData.append('Company', employeeData.Company);
      employeeData.RoleId !== undefined && formData.append('RoleId', employeeData.RoleId);
      employeeData.employmentType !== undefined && formData.append('employmentType', employeeData.employmentType);
      employeeData.workPreference !== undefined && formData.append('workPreference', employeeData.workPreference);
      employeeData.employee_status !== undefined && formData.append('employee_status', employeeData.employee_status);
      employeeData.pfEnable !== undefined && formData.append('pfEnable', employeeData.pfEnable);
      employeeData.job_location !== undefined && formData.append('job_location', employeeData.job_location);
      employeeData.branch !== undefined && formData.append('branch', employeeData.branch);
      employeeData.checked !== undefined && formData.append('checked', employeeData.checked);
      employeeData.locationTracking !== undefined && formData.append('locationTracking', employeeData.locationTracking);
      employeeData.experiencelabel !== undefined && formData.append('experiencelabel', employeeData.experiencelabel);
      employeeData.hrId !== undefined && formData.append('hrId', employeeData.hrId);
      employeeData.hod_id !== undefined && formData.append('hod_id', employeeData.hod_id);

      // Handle file separately
      employeeData.profilePicture && formData.append('file', employeeData.profilePicture);

      const response = await employeeServiceApiInstance.put(
        `/employees/${employeeId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating employee');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);



export const updateBankDetails = createAsyncThunk<any, { employeeId: string; bankDetails: any }, { rejectValue: string }>(
  'employee/updateBankDetails',
  async ({ employeeId, bankDetails }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/${employeeId}/bank-details`, bankDetails);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data || 'Error updating bank details');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const createSpouse = createAsyncThunk<any, { employeeId: string; spouseData: any }, { rejectValue: string }>(
  'employee/createSpouse',
  async ({ employeeId, spouseData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/${employeeId}/spouse`, spouseData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating spouse');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

// Create Child
export const createChild = createAsyncThunk<any, { employeeId: string; childData: any }, { rejectValue: string }>(
  'employee/createChild',
  async ({ employeeId, childData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/${employeeId}/child`, childData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating child');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

// Update Child
export const updateChild = createAsyncThunk<any, { Id: number; childData: any }, { rejectValue: string }>(
  'employee/updateChild',
  async ({ Id, childData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/child/${Id}`, childData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating child');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
// Create Education
export const createEducation = createAsyncThunk<any, { employeeId: string; educationData: any }, { rejectValue: string }>(
  'employee/createEducation',
  async ({ employeeId, educationData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/${employeeId}/education`, educationData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating education');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

// Update Education
export const updateEducation = createAsyncThunk<any, { Id: string; educationData: any }, { rejectValue: string }>(
  'employee/updateEducation',
  async ({ Id, educationData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/education/${Id}`, educationData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating education');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

// Create Parent
export const createParent = createAsyncThunk<any, { employeeId: string; parentData: any }, { rejectValue: string }>(
  'employee/createParent',
  async ({ employeeId, parentData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/${employeeId}/parents`, parentData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating parent information');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

// Update Parent
export const updateParent = createAsyncThunk<any, { Id: string; parentData: any }, { rejectValue: string }>(
  'employee/updateParent',
  async ({ Id, parentData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/parents/${Id}`, parentData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating parent information');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const createTopSkill = createAsyncThunk<any, { employeeId: string; topSkillData: any }, { rejectValue: string }>(
  'employee/createTopSkill',
  async ({ employeeId, topSkillData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/${employeeId}/skill`, topSkillData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating top skill');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const updateTopSkill = createAsyncThunk<any, { Id: number; topSkillData: any }, { rejectValue: string }>(
  'employee/updateTopSkill',
  async ({ Id, topSkillData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/skill/${Id}`, topSkillData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating top skill');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const createExperience = createAsyncThunk<any, { employeeId: string; experienceData: any }, { rejectValue: string }>(
  'employee/createExperience',
  async ({ employeeId, experienceData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/${employeeId}/experience`, experienceData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating experience');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const updateExperience = createAsyncThunk<any, { Id: any; experienceData: any }, { rejectValue: string }>(
  'employee/updateExperience',
  async ({ Id, experienceData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/experience/${Id}`, experienceData);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating experience');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const getEmployees = createAsyncThunk<CreateEmployeeResponse, void, { rejectValue: string }>(
  'employee/getEmployees',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get('/employees');
      return response.data as CreateEmployeeResponse;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error fetching employees');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const FetchAlljobLocation = createAsyncThunk<any, void, { rejectValue: string }>(
  'employee/Joblocation',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get('/employees/branch/job-location');
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error fetching Job location');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const getEmployeeById = createAsyncThunk<any, { employeeId: string }, { rejectValue: string }>(
  'employee/getEmployeeById',
  async ({ employeeId }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employees/${employeeId}`);
      return response.data as any;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error fetching employee details');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);
export const getUppderLevelById = createAsyncThunk<any, { employeeId: any }, { rejectValue: string }>(
  'employee/getUpperLevelById',
  async ({ employeeId }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employees/upperlevel/${employeeId}`);
      return response.data as any;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error fetching employee details');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const getEmployeeByUserId = createAsyncThunk<any, { userId: string }, { rejectValue: string }>(
  'employee/getEmployeeByUserId',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employees/${userId}/user`);
      return response.data as any;
    } catch (error: any) {
      if (error.response) {
        // Return specific error from the response, or fallback to a general error message
        return rejectWithValue(error.response.data.error || 'Error fetching employee details');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const getEmployeeBySupervisorId = createAsyncThunk<any, { userId: string }, { rejectValue: string }>(
  'employee/getEmployeeBySupervisorId',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employees/supervisorId/${userId}`);
      return response.data as any;
    } catch (error: any) {
      if (error.response) {
        // Return specific error from the response, or fallback to a general error message
        return rejectWithValue(error.response.data.error || 'Error fetching employee details');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);

export const createEmployeeDocument = createAsyncThunk<
  any,
  { employeeId: string; documentData: FormData },
  { rejectValue: string }
>(
  'employee/createEmployeeDocument',
  async ({ employeeId, documentData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(
        `/employees/${employeeId}/document`,
        documentData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error creating employee document');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);


export const updateEmployeeDocument = createAsyncThunk<
  any,
  { Id: number; documentData: FormData },
  { rejectValue: string }
>(
  'employee/updateEmployeeDocument',
  async ({ Id, documentData }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(
        `/employees/document/${Id}`,
        documentData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data.error || 'Error updating employee document');
      }
      return rejectWithValue('Something went wrong');
    }
  }
);



// Delete Client
export const deleteEmployee = createAsyncThunk(
  'clients/deleteClient',
  async ({ employeeId }: any, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.delete(`/employees/${employeeId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete client');
    }
  }
);

// Fetch Employee Analytics
export const fetchEmployeeAnalytics = createAsyncThunk(
  'Employee/fetchEmployeeAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get('/employee-analytics');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch employee analytics');
    }
  }
);

export const fetchEmployeeOrganizationTree = createAsyncThunk(
  'Employee/fetchEmployeeOrganizationTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get('/employees/tree/hierarchy');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch employee analytics');
    }
  }
);


export const createemployeeleave = createAsyncThunk(
  'performance/createachievement',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post(`/employees/employeeleave`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch CreateLeave');
    }
  }
);
export const updateemployeeleave = createAsyncThunk(
  'performance/updateemployeeleave',
  async ({ data, Id }: any, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.put(`/employees/employeeleave/${Id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch CreateLeave');
    }
  }
);
export const deleteemployeeleave = createAsyncThunk(
  'performance/deleteemployeeleave',
  async ({ Id, employeeId }: { Id: any; employeeId: number }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.delete(
        `/employees/employeeleave/${Id}`,
        {
          params: {
            employeeId, // <- sent as query param
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete leave');
    }
  }
);
export const fetchEmployeeTrackingList = createAsyncThunk(
  'employees/fetchTrackingList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(
        `/employees/location/tracking-list`,

      );
      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch tracking list'
      );
    }
  }
);

//  Fetch employee location (current month)
export const fetchEmployeeLocation = createAsyncThunk(
  'employees/fetchLocation',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(
        `/employee/location/${employeeId}`,
      );
      return {
        employeeId,
        locations: response.data?.data || [],
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch employee location'
      );
    }
  }
);

export const deleteEmployeeExperience = createAsyncThunk(
  'employee/deleteEmployeeExperience',
  async ({ Id, employeeId }: { Id: number; employeeId: number }, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.delete(
        `/employees/experience/${Id}`,
        {
          params: {
            employeeId, // sent as query param
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete experience');
    }
  }
);

export const deleteEmployeeSkill = createAsyncThunk(
  "employee/deleteEmployeeSkill",
  async (
    { Id, employeeId }: { Id: number; employeeId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await employeeServiceApiInstance.delete(
        `/employees/skill/${Id}`,
        {
          params: {
            employeeId, // sent as query param
          },
        }
      );
      return response.data; // assuming API returns {status, message, data}
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to delete skill");
    }
  }
);


export const deleteEmployeeChild = createAsyncThunk(
  "employee/deleteEmployeeChild",
  async (
    { Id, employeeId }: { Id: number; employeeId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await employeeServiceApiInstance.delete(
        `/employees/child/${Id}`,
        {
          params: {
            employeeId, // sent as query param
          },
        }
      );
      return response.data; // Expecting {status, message, data}
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to delete child");
    }
  }
);


export const deleteEmployeeDocument = createAsyncThunk(
  "employee/deleteEmployeeDocument",
  async (
    { Id, employeeId }: { Id: number; employeeId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await employeeServiceApiInstance.delete(
        `/employees/document/${Id}`,
        {
          params: {
            employeeId, // sent as query param
          },
        }
      );
      return response.data; // Expecting {status, message, data}
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to delete document");
    }
  }
);

export const deleteEmployeeProfilePic = async (employeeId: number) => {
  try {
    const res = await employeeServiceApiInstance.delete(
      `/employees/${employeeId}/picture`
    );
    return res.data;
  } catch (error: any) {
    console.error("Delete error:", error.response?.data || error);
    throw error.response?.data || "Failed to delete profile picture";
  }
};

export const getEmployeeDetailsByManagerOrHod = createAsyncThunk(
  "employee/getEmployeeDetailsByManagerOrHod",
  async (employeeId: number, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employeesDetails/SupOrHod/${employeeId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch employee details");
    }
  }
)

export const hierarchyForSupervisorAndHod = createAsyncThunk(
  "employee/hierarchyForSupervisorAndHod",
  async (employeeId: any, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get(`/employeesHierarchy/SupOrHod?employeeId=${employeeId}`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch employee details");
    }
  }
)

export const createDynamicRequest = createAsyncThunk(
  "employee/createDynamicRequest",
  async (payload: FormData, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.post("/dynamic-requests", payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to create dynamic request"
      );
    }
  }
);

export const fetchDynamicRequestsForSupervisor = createAsyncThunk(
  "dynamicRequest/fetchForSupervisor",
  async (
    { supervisorId }: { supervisorId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await employeeServiceApiInstance.get(
        `/dynamic-requests/supervisor/${supervisorId}`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch dynamic requests"
      );
    }
  }
);

export const fetchAllDynamicRequests = createAsyncThunk(
  "dynamicRequest/fetchAll",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await employeeServiceApiInstance.get("/fetchAllDynamicRequests");
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch dynamic requests"
      );
    }
  }
);

export const updateDynamicRequest = createAsyncThunk("employee/updateDynamicRequest",
  async (
    {Id,Status,RejectDescription,modifiedBy,}: {
      Id: number;
      Status: "Pending" | "Approved" | "Rejected";
      RejectDescription?: string;
      modifiedBy: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await employeeServiceApiInstance.put(`/dynamic-requests/update/${Id}`
        ,{ status: Status, rejectDescription: RejectDescription, modifiedBy } 
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update dynamic request"
      );
    }
  }
);








